import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import api, { formatApiError } from "../lib/api";

const blankSettlement = {
  main_debt: "0",
  interest_amount: "0",
  penalty_amount: "0",
  referral_commission: "0",
  settled_date: "",
  settle_remark: "",
};

export default function EditSettlementDialog({ loanId, settlement, onClose, onDone }) {
  const [form, setForm] = useState(blankSettlement);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settlement) {
      return;
    }
    setForm({
      main_debt: String(settlement.main_debt ?? 0),
      interest_amount: String(settlement.settlement_interest ?? 0),
      penalty_amount: String(settlement.settlement_penalty ?? 0),
      referral_commission: String(settlement.settlement_referral_commission ?? 0),
      settled_date: settlement.payment_date || "",
      settle_remark: settlement.note?.split("\n").slice(-1)[0] || "",
    });
  }, [settlement]);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveSettlement = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.put(`/loans/${loanId}/settlement`, {
        main_debt: Number(form.main_debt || 0),
        interest_amount: Number(form.interest_amount || 0),
        penalty_amount: Number(form.penalty_amount || 0),
        referral_commission: Number(form.referral_commission || 0),
        settled_date: form.settled_date,
        settle_remark: form.settle_remark,
      });
      toast.success("Settlement updated");
      onDone();
      onClose();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(settlement)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="edit-settlement-dialog">
        <DialogHeader>
          <DialogTitle>Edit settlement</DialogTitle>
          <DialogDescription>
            Update the settlement amounts, date, or remark. Balances and reports recalculate.
          </DialogDescription>
        </DialogHeader>
        <form className="dialog-form" onSubmit={saveSettlement}>
          <Label htmlFor="edit-settlement-main-debt">Main Debt</Label>
          <Input
            id="edit-settlement-main-debt"
            data-testid="edit-settlement-main-debt-input"
            type="number"
            min="0"
            step="0.01"
            value={form.main_debt}
            onChange={(event) => update("main_debt", event.target.value)}
          />
          <Label htmlFor="edit-settlement-interest">Interest</Label>
          <Input
            id="edit-settlement-interest"
            data-testid="edit-settlement-interest-input"
            type="number"
            min="0"
            step="0.01"
            value={form.interest_amount}
            onChange={(event) => update("interest_amount", event.target.value)}
          />
          <Label htmlFor="edit-settlement-penalty">Penalty</Label>
          <Input
            id="edit-settlement-penalty"
            data-testid="edit-settlement-penalty-input"
            type="number"
            min="0"
            step="0.01"
            value={form.penalty_amount}
            onChange={(event) => update("penalty_amount", event.target.value)}
          />
          <Label htmlFor="edit-settlement-referral">Referral Commission</Label>
          <Input
            id="edit-settlement-referral"
            data-testid="edit-settlement-referral-input"
            type="number"
            min="0"
            step="0.01"
            value={form.referral_commission}
            onChange={(event) => update("referral_commission", event.target.value)}
          />
          <Label htmlFor="edit-settlement-date">Settled Date</Label>
          <Input
            id="edit-settlement-date"
            data-testid="edit-settlement-date-input"
            type="date"
            value={form.settled_date}
            onChange={(event) => update("settled_date", event.target.value)}
            required
          />
          <Label htmlFor="edit-settlement-remark">Settlement Remark</Label>
          <Textarea
            id="edit-settlement-remark"
            data-testid="edit-settlement-remark-input"
            value={form.settle_remark}
            onChange={(event) => update("settle_remark", event.target.value)}
            required
          />
          <div className="dialog-actions">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-edit-settlement-button"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="save-edit-settlement-button">
              {saving ? "Saving…" : "Save settlement"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}